/* ============================================================================
   Pincode check — shared by the product page and the cart.

   This lived inside furniva-product.js, which only the product section loads.
   The snippet renders in both places, so on the cart the Check button had no
   handler and pressing it did nothing. The controller now ships with the
   snippet instead, so wherever the snippet goes the behaviour goes with it.

   Two rules this file keeps:

     · It never submits or mutates the cart, and never navigates to checkout.
       It is a lookup, and the cart form must not notice it happening.
     · It reports what it is told. With no endpoint configured, stock, delivery
       and installation are three SEPARATE unconfirmed results plus a contact
       link — never an exact date, never a serviceability claim in either
       direction.

   Every `[data-fv-pincode]` panel on the page is wired, each with its own
   state, so rendering the snippet twice is safe.
   ============================================================================ */
(function () {
  'use strict';

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }
  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }
  function on(el, type, handler, options) {
    if (el) el.addEventListener(type, handler, options);
  }
  function hapticSuccess() {
    if ('vibrate' in navigator) navigator.vibrate(18);
  }

  /* --------------------------------------------------------------- memory --
     The same question, asked again.

     A shopper checks their pincode on the product page, opens the cart and
     checks it again, changes a quantity and checks once more. Each of those was
     a fresh round trip through the Worker to the CRM — measured at 0.9–2.5s
     warm, and 21.5s against a Fly machine that had gone to sleep.

     So a confirmed answer is remembered for the page's lifetime, against the
     pincode AND the exact lines it was asked about, since two warehouses mean
     two deliveries and that is part of the answer. The store is module-level
     rather than per panel because the cart rebuilds its markup on every change
     and the panel that comes back is a different element.

     Only a decided answer is kept. `serviceable === null` means nobody could
     say, and that is a question to ask again, not a result to hold on to.
     Nothing generic is ever substituted for the real delivery-master answer.
   * ----------------------------------------------------------------------- */
  var answers = {};
  var pending = {};
  var TIMEOUT_MS = 12000;

  function wire(panel) {
    if (panel.dataset.fvPincodeBound) return;
    panel.dataset.fvPincodeBound = '1';

    var endpoint = panel.dataset.fvPincodeEndpoint;
    var input = $('[data-fv-pincode-input]', panel);
    var button = $('[data-fv-pincode-check]', panel);
    var results = $('[data-fv-pincode-results]', panel);
    if (!input || !button || !results) return;

    var TONE = {
      yes: { icon: 'check-circle', className: 'fv-note--success' },
      no: { icon: 'alert-circle', className: 'fv-note--error' },
      unknown: { icon: 'info', className: 'fv-note--notice' }
    };

    function row(title, payload, index) {
      var status = (payload && payload.status) || 'unknown';
      var tone = TONE[status] || TONE.unknown;
      var detail = (payload && payload.detail) || '';
      var span = document.createElement('div');
      span.className = 'fv-note fv-pin__result-card ' + tone.className;
      span.style.setProperty('--fv-pin-delay', ((index || 0) * 340) + 'ms');
      var copy = document.createElement('span');
      var strong = document.createElement('strong');
      strong.textContent = title;
      copy.appendChild(strong);
      copy.appendChild(document.createTextNode(detail ? ' — ' + detail : ''));
      span.appendChild(copy);
      return span;
    }

    function replacementRow() {
      var card = row('Hassle-free replacement', {
        status: 'unknown',
        detail: 'Damage or exchange? We’ll help.'
      }, 3);
      card.classList.remove('fv-note--notice');
      card.classList.add('fv-note--neutral');
      var link = document.createElement('a');
      link.href = '/pages/returns-policy';
      link.className = 'fv-pin__terms';
      link.textContent = 'Terms';
      card.querySelector('span').appendChild(document.createTextNode(' '));
      card.querySelector('span').appendChild(link);
      return card;
    }

    var lastChecked = null;

    function check() {
      var value = (input.value || '').trim();
      lastChecked = value;
      if (!/^\d{6}$/.test(value)) {
        results.innerHTML =
          '<div class="fv-note fv-note--error"><span>Enter a 6-digit pincode.</span></div>';
        input.focus();
        return;
      }

      results.innerHTML =
        '<div class="fv-note fv-note--neutral"><span class="fv-spin"></span><span>Checking ' +
        value +
        '</span></div>';

      if (!endpoint) {
        results.innerHTML = '';
        results.appendChild(row(panel.dataset.fvStockLabel || 'Availability', {
          status: panel.dataset.fvStockState || 'unknown',
          detail: panel.dataset.fvStockDetail || ''
        }, 0));
        results.appendChild(row('Delivery', {
          status: 'unknown',
          detail: 'Your delivery range for ' + value + ' needs confirmation.'
        }, 1));
        results.appendChild(row('Installation', {
          status: 'unknown',
          detail: 'Free installation where covered. We will confirm it for ' + value + '.'
        }, 2));
        results.appendChild(replacementRow());
        var contact = document.createElement('a');
        contact.className = 'fv-btn fv-btn--secondary fv-btn--sm';
        contact.href = (panel.dataset.fvContactUrl || '/pages/contact') + '?pincode=' + encodeURIComponent(value);
        contact.textContent = 'Ask us to confirm this pincode';
        results.appendChild(contact);
        remember(value, null);
        panel.classList.add('is-checked');
        hapticSuccess();
        return;
      }

      /* POST, not GET: a real Cargo quote needs the cart lines, because
         packages and dispatch warehouse are resolved per SKU. The endpoint is
         the account Worker, which holds the CRM service token; the browser
         never calls CRM and never sees a SKU-level blocker. */
      var lines = [];
      try {
        lines = JSON.parse(panel.dataset.fvPincodeLines || '[]');
      } catch (error) {
        lines = [];
      }

      var key = value + '|' + JSON.stringify(lines);

      // Already answered, or already being answered. Two presses of Check, or a
      // second panel on the same page, join the request in flight rather than
      // starting another one.
      var known = answers[key];
      if (known) {
        paint(known);
        return;
      }

      button.disabled = true;
      var request = pending[key];
      if (!request) {
        request = ask(value, lines, key);
        pending[key] = request;
      }

      request
        .then(paint)
        .catch(failed)
        .then(function () {
          button.disabled = false;
        });

      function paint(data) {
        results.innerHTML = '';
        results.appendChild(row(panel.dataset.fvStockLabel || 'Availability', {
          status: panel.dataset.fvStockState || 'unknown',
          detail: panel.dataset.fvStockDetail || ''
        }, 0));

        var delivery = { status: 'unknown', detail: 'Your delivery range for ' + value + ' needs confirmation.' };
        if (data.serviceable === true) {
          // Free everywhere we serve, so this line is about time, never cost.
          var span = data.delivery_estimate;
          if (!span && data.delivery_days_min && data.delivery_days_max) {
            span = data.delivery_days_min + '\u2013' + data.delivery_days_max + ' calendar days';
          }
          /* "from dispatch", matching the Help Centre's approved wording:
             "an estimated calendar-day range from dispatch — not an exact
             date". The delivery master states calendar days but not the
             start point, so this does not invent an order-to-door promise. */
          delivery = {
            status: 'yes',
            detail: span
              ? 'Free delivery, ' + span + ' from dispatch.'
              : 'Free delivery to this pincode.'
          };
          if (data.shipment_count > 1) {
            delivery.detail += ' This order arrives in ' + data.shipment_count + ' deliveries.';
          }
          /* Ships from the nearest warehouse that holds the item. */
          var cities = [];
          (data.stock || []).forEach(function (line) {
            if (line && line.in_stock === true && line.ships_from && cities.indexOf(line.ships_from) === -1) {
              cities.push(line.ships_from);
            }
          });
          if (cities.length) {
            delivery.detail += ' Ships from ' + cities.join(' and ') + '.';
          }
        } else if (data.serviceable === false) {
          delivery = { status: 'no', detail: data.reason || 'We do not deliver to that pincode yet.' };
        }
        results.appendChild(row('Delivery', delivery, 1));

        /* Installation is a separate register with its own answer. Unknown
           renders as "being confirmed", never as availability. */
        var install = { status: 'unknown', detail: 'Free installation where covered. We will confirm it for ' + value + '.' };
        if (data.installation && data.installation.message) {
          var state = data.installation.status;
          install = {
            status: state === 'confirmed' ? 'yes' : state === 'unavailable' ? 'no' : 'unknown',
            detail: data.installation.message
          };
        }
        results.appendChild(row('Installation', install, 2));
        results.appendChild(replacementRow());

        /* The CRM could not decide. Say so and offer the one useful act, rather
           than leaving a dead "needs confirmation" the customer cannot move
           past. Never a serviceability claim in either direction. */
        if (data.serviceable !== true && data.serviceable !== false) {
          results.appendChild(retryRow(data.timed_out));
        }

        // The verdict travels with the pincode, so every checkout button on
        // the site can refuse an address we cannot deliver to
        // (furniva-checkout-gate.js). Only a definite answer is kept.
        remember(value, data.place, data.serviceable === true ? true : data.serviceable === false ? false : null);
        /* Once the check has a real answer, the cart's generic "confirmed from
           your pincode" and "free installation where covered" lines repeat
           less than the panel now says, so they step aside (furniva-cart.css). */
        var root = document.documentElement;
        if (data.serviceable === true) root.setAttribute('data-fv-delivery', 'known');
        else root.removeAttribute('data-fv-delivery');
        if (install.status === 'yes' || install.status === 'no') root.setAttribute('data-fv-install', 'known');
        else root.removeAttribute('data-fv-install');
        panel.classList.add('is-checked');
        hapticSuccess();
      }

      /* A lookup that never completed. Also never a "no": the pincode may well
         be served, and telling someone it is not would lose an order they were
         ready to place. */
      function failed(error) {
        results.innerHTML = '';
        document.documentElement.removeAttribute('data-fv-delivery');
        document.documentElement.removeAttribute('data-fv-install');
        var note = document.createElement('div');
        note.className = 'fv-note fv-note--notice';
        var text = document.createElement('span');
        text.textContent = error && error.fvTimeout
          ? 'That check took longer than it should have. Your order is not affected — try again, or ask us to confirm.'
          : 'We could not check that pincode just now. Your order is not affected — ask us and we will confirm delivery and installation for you.';
        note.appendChild(text);
        results.appendChild(note);
        results.appendChild(retryRow(Boolean(error && error.fvTimeout)));
      }

      function retryRow(timedOut) {
        var wrap = document.createElement('div');
        wrap.className = 'fv-pin__retry';

        var again = document.createElement('button');
        again.type = 'button';
        again.className = 'fv-btn fv-btn--secondary fv-btn--sm';
        again.textContent = timedOut ? 'Try again' : 'Check again';
        again.addEventListener('click', function () {
          // A fresh ask, not the remembered miss.
          delete answers[key];
          delete pending[key];
          check();
        });
        wrap.appendChild(again);

        var contact = document.createElement('a');
        contact.className = 'fv-btn fv-btn--ghost fv-btn--sm';
        contact.href = (panel.dataset.fvContactUrl || '/pages/contact') + '?pincode=' + encodeURIComponent(value);
        contact.textContent = 'Ask us to confirm';
        wrap.appendChild(contact);

        return wrap;
      }
    }

    /* One request, whoever asked. Resolves with the payload and remembers only
       a decided answer; the promise itself is shared while it is in flight so
       two panels and two presses cost one round trip. */
    function ask(value, lines, key) {
      var controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
      var expired = false;
      var timer = controller
        ? setTimeout(function () {
            expired = true;
            controller.abort();
          }, TIMEOUT_MS)
        : null;

      return window.fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ pincode: value, lines: lines }),
        signal: controller ? controller.signal : undefined
      })
        .then(
          function (response) {
            clearTimeout(timer);
            if (!response.ok) throw new Error('Pincode lookup failed: ' + response.status);
            return response.json();
          },
          function (error) {
            clearTimeout(timer);
            var failure = new Error(expired ? 'pincode lookup timed out' : 'pincode lookup failed');
            failure.fvTimeout = expired;
            failure.cause = error;
            throw failure;
          }
        )
        .then(function (data) {
          delete pending[key];
          if (data && (data.serviceable === true || data.serviceable === false)) {
            answers[key] = data;
          }
          return data;
        }, function (error) {
          delete pending[key];
          throw error;
        });
    }

    on(button, 'click', check);
    on(input, 'keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        check();
      }
    });

    /* A pincode given anywhere — the header's "Deliver to", another product,
       the cart — is checked here straight away, so delivery, stock and
       installation for it show without asking twice. Owner requirement,
       2026-09-30: the customer never enters the pincode again. lastChecked
       stops the check's own "pincode saved" event from starting another. */
    function checkFor(pincode) {
      if (!/^\d{6}$/.test(pincode || '') || pincode === lastChecked) return;
      if (document.activeElement === input && input.value && input.value !== pincode) return;
      input.value = pincode;
      check();
    }
    document.addEventListener('furniva:deliver-to', function (event) {
      // The cart redraws its panel; a replaced one stays quiet.
      if (event.detail && panel.isConnected) checkFor(event.detail.pincode);
    });

    var remembered = recall();
    if (remembered && !input.value) checkFor(remembered.pincode);
  }

  /* One pincode for the whole site.

     The mobile header's "Deliver to", the product page and the cart all read
     and write the same record, so a pincode entered in any of them is the one
     the others show. It lives on this device only — localStorage, so it
     survives the tab — and is never sent anywhere except with a check the
     shopper asked for. The place is the Department of Posts district the CRM
     returned, or null; it is never guessed. furniva-deliver-to.js owns the
     header and uses the same key and the same event. */
  var DELIVER_KEY = 'furniva:deliver-to';
  function recall() {
    try {
      var saved = JSON.parse(localStorage.getItem(DELIVER_KEY) || 'null');
      if (saved && /^\d{6}$/.test(saved.pincode)) return saved;
    } catch (error) {}
    try {
      var legacy = sessionStorage.getItem('furniva:pincode');
      if (legacy && /^\d{6}$/.test(legacy)) return { pincode: legacy, city: null };
    } catch (error) {}
    return null;
  }
  function remember(pincode, place, serviceable) {
    var record = { pincode: pincode, city: place && place.city ? String(place.city) : null, at: Date.now() };
    if (serviceable === true || serviceable === false) record.serviceable = serviceable;
    try { localStorage.setItem(DELIVER_KEY, JSON.stringify(record)); } catch (error) {}
    try { sessionStorage.setItem('furniva:pincode', pincode); } catch (error) {}
    try { document.dispatchEvent(new CustomEvent('furniva:deliver-to', { detail: record })); } catch (error) {}
  }

  $$('[data-fv-pincode]').forEach(wire);

  // The cart replaces its own markup after every quantity change, removal and
  // addon add, so the panel that comes back is a different element.
  document.addEventListener('cart:refresh', function () {
    $$('[data-fv-pincode]').forEach(wire);
  });
})();
